import { Link } from "react-router-dom";

function TocItem({ id, label }) {
  return (
    <li>
      <a href={`#${id}`} className="text-[#5DADE2] font-semibold hover:underline">{label}</a>
    </li>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mt-10">
      <h2 className="text-xl sm:text-2xl font-extrabold text-[#243B53]">{title}</h2>
      <div className="mt-3 space-y-3 text-[#243B53]/85 leading-relaxed text-[15px]">
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto p-6 sm:p-10" data-testid="privacy-page">
      <Link to="/" data-testid="privacy-back-link" className="text-[#5DADE2] font-bold text-sm">← Voltar ao Clubinho</Link>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-[#243B53] mt-4">Política de Privacidade</h1>
      <p className="text-sm text-[#243B53]/60 mt-2">Última atualização: setembro de 2026</p>

      <div className="card-soft p-5 mt-6 bg-[#FFF9ED]">
        <p className="text-sm text-[#243B53]/85">
          Esta Política descreve como o <b>Clubinho da Fé</b>, plataforma digital de educação
          cristã infantil, trata dados pessoais no fornecimento do produto. Ao contratar a
          assinatura ou utilizar a plataforma, você deve ler este documento em conjunto com
          os nossos <Link to="/terms" className="text-[#5DADE2] font-bold">Termos de Uso</Link>.
        </p>
      </div>

      <nav className="card-soft p-5 mt-6" aria-label="Sumário">
        <p className="text-sm font-extrabold text-[#243B53] uppercase tracking-wide">Sumário</p>
        <ul className="mt-3 space-y-1 text-sm columns-1 sm:columns-2">
          <TocItem id="controlador" label="1. Controlador" />
          <TocItem id="escopo" label="2. Escopo desta Política" />
          <TocItem id="dados" label="3. Dados que podem ser tratados" />
          <TocItem id="finalidades" label="4. Finalidades do tratamento" />
          <TocItem id="bases" label="5. Bases legais" />
          <TocItem id="criancas" label="6. Crianças e adolescentes" />
          <TocItem id="compartilhamento" label="7. Compartilhamento e operadores" />
          <TocItem id="transferencias" label="8. Transferências internacionais" />
          <TocItem id="seguranca" label="9. Segurança da informação" />
          <TocItem id="retencao" label="10. Retenção" />
          <TocItem id="direitos" label="11. Direitos dos titulares" />
          <TocItem id="cookies" label="12. Cookies e tecnologias técnicas" />
          <TocItem id="alteracoes" label="13. Alterações desta Política" />
          <TocItem id="contato" label="14. Contato" />
        </ul>
      </nav>

      <Section id="controlador" title="1. Controlador dos dados">
        <p>
          O controlador dos dados pessoais tratados no âmbito do Clubinho da Fé é a
          <b> CREATIVE SHOOP</b>, inscrita no CNPJ nº <b>65.465.060/0001-25</b>,
          responsável pela plataforma disponibilizada em
          {" "}<a href="https://clubinhodafeof.com" className="text-[#5DADE2] font-bold" target="_blank" rel="noopener noreferrer">clubinhodafeof.com</a>.
        </p>
      </Section>

      <Section id="escopo" title="2. Escopo desta Política">
        <p>
          Esta Política aplica-se ao tratamento de dados pessoais realizado no fornecimento
          da plataforma Clubinho da Fé, incluindo o site institucional, a área de assinante
          logada, os perfis infantis administrados pelo responsável, os fluxos de assinatura
          via checkout Eduzz e as comunicações transacionais operadas por Resend. Não se
          aplica a sites, aplicativos ou serviços de terceiros que possam ser acessados a
          partir de links externos.
        </p>
      </Section>

      <Section id="dados" title="3. Dados que podem ser tratados">
        <p><b>3.1. Dados do responsável/assinante:</b></p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Nome e endereço de e-mail;</li>
          <li>Informações de conta (senha em formato protegido por <i>hash</i>, avatar, papel de usuário);</li>
          <li>Informações relacionadas à assinatura (status, data de ativação, ciclo, identificadores);</li>
          <li>Identificadores necessários à integração com Eduzz (por exemplo, contrato, assinatura, cliente);</li>
          <li>Registros de acesso, tais como data/hora de login, endereço IP e dados técnicos do dispositivo/navegador;</li>
          <li>Dados de segurança (tentativas de acesso, tokens temporários de ativação/redefinição, prevenção a fraude).</li>
        </ul>
        <p className="mt-3 text-sm text-[#243B53]/70">
          O Clubinho da Fé <b>não armazena</b> dados completos de cartão de crédito. As
          informações de pagamento são tratadas diretamente pela plataforma de pagamento
          utilizada no checkout (Eduzz), conforme a política de privacidade daquela
          plataforma.
        </p>

        <p className="mt-4"><b>3.2. Dados dos perfis infantis:</b></p>
        <p>
          Os perfis infantis são criados e administrados pelo adulto responsável.
          Coletamos apenas o mínimo necessário à experiência educacional:
        </p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Nome ou apelido do perfil;</li>
          <li>Avatar (imagem ilustrativa opcional);</li>
          <li>Faixa etária ou idade, quando informada pelo responsável;</li>
          <li>Progresso educacional e atividades concluídas;</li>
          <li>Pontos, estrelas, conquistas, jornadas, missões e favoritos;</li>
          <li>Preferências necessárias à personalização apropriada por faixa etária.</li>
        </ul>
        <p className="mt-3 text-sm text-[#243B53]/70">
          <b>Não coletamos</b>, no âmbito do Clubinho da Fé: documentos de identificação
          de crianças, dados biométricos, localização precisa, contatos de amigos ou colegas,
          informações escolares, dados de saúde ou fotos pessoais reais das crianças.
        </p>
      </Section>

      <Section id="finalidades" title="4. Finalidades do tratamento">
        <ul className="list-disc ml-6 space-y-1">
          <li>Criação, administração e segurança da conta do responsável;</li>
          <li>Confirmação e administração da assinatura, incluindo integração com o processador de pagamento;</li>
          <li>Disponibilização do conteúdo contratado (histórias, atividades, jornadas, séries, devocionais e materiais);</li>
          <li>Manutenção dos perfis infantis, do progresso educacional e da gamificação (Passaporte da Fé);</li>
          <li>Segurança da plataforma e prevenção a fraude, abuso e acesso não autorizado;</li>
          <li>Comunicações transacionais (ativação de conta, redefinição de senha, avisos operacionais);</li>
          <li>Atendimento e suporte;</li>
          <li>Cumprimento de obrigações legais, regulatórias e exercício regular de direitos;</li>
          <li>Melhoria operacional da plataforma, quando aplicável e observadas as bases legais.</li>
        </ul>
      </Section>

      <Section id="bases" title="5. Bases legais">
        <p>
          Cada operação de tratamento é realizada com fundamento em base legal específica
          prevista na Lei nº 13.709/2018 (LGPD), aplicando-se, conforme o caso:
        </p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Execução do contrato de assinatura e procedimentos preliminares relacionados;</li>
          <li>Cumprimento de obrigação legal ou regulatória;</li>
          <li>Exercício regular de direitos em processo judicial, administrativo ou arbitral;</li>
          <li>Legítimo interesse do controlador ou de terceiros, quando juridicamente aplicável e após análise das expectativas do titular e das salvaguardas;</li>
          <li>Consentimento do titular, ou do responsável legal, quando exigido pela legislação, notadamente em situações envolvendo crianças e adolescentes.</li>
        </ul>
        <p className="text-sm text-[#243B53]/70 mt-2">Nenhuma base legal cobre isoladamente todo o tratamento — cada finalidade é analisada individualmente.</p>
      </Section>

      <Section id="criancas" title="6. Crianças e adolescentes">
        <p>
          O Clubinho da Fé é destinado ao contexto <b>familiar e educacional cristão infantil</b>.
          A contratação da assinatura e a criação/gerenciamento dos perfis são realizadas
          por adulto responsável. O tratamento de dados de crianças e adolescentes observa
          prioritariamente o seu <b>melhor interesse</b>, na forma da LGPD, do Estatuto da
          Criança e do Adolescente e das obrigações aplicáveis do Estatuto Digital da
          Criança e do Adolescente (Lei nº 15.211/2025 e regulamentação vigente).
        </p>
        <p>
          Comprometemo-nos a tratar exclusivamente dados adequados, pertinentes e
          necessários às funcionalidades oferecidas. Quando a legislação exigir consentimento
          específico e em destaque do responsável, este será solicitado. O responsável poderá,
          a qualquer tempo, solicitar informações, correção ou exclusão dos dados do perfil
          infantil, conforme a legislação aplicável.
        </p>
        <p className="text-sm text-[#243B53]/70">
          O uso da plataforma por crianças deve ocorrer sob supervisão apropriada do
          responsável. Não afirmamos conformidade absoluta ou certificação governamental;
          buscamos, de forma diligente, atender às obrigações vigentes e revisar
          periodicamente nossas práticas.
        </p>
      </Section>

      <Section id="compartilhamento" title="7. Compartilhamento e operadores">
        <p>Podemos compartilhar dados pessoais, na medida estritamente necessária, com:</p>
        <ul className="list-disc ml-6 space-y-1">
          <li><b>Eduzz</b> — processamento comercial, checkout, administração de assinatura e eventos de pagamento;</li>
          <li><b>Resend</b> — envio de comunicações transacionais;</li>
          <li>Provedores de infraestrutura, hospedagem e banco de dados necessários ao funcionamento da aplicação;</li>
          <li>Fornecedores técnicos estritamente necessários à operação, segurança ou obrigações legais;</li>
          <li>Autoridades competentes, quando exigido por lei ou ordem judicial.</li>
        </ul>
        <p className="mt-2"><b>Não vendemos dados pessoais.</b></p>
      </Section>

      <Section id="transferencias" title="8. Transferências internacionais">
        <p>
          Alguns fornecedores tecnológicos podem processar dados em outros países,
          observadas as salvaguardas exigidas pela legislação aplicável, tais como
          cláusulas contratuais adequadas e adoção de medidas técnicas e organizacionais
          equivalentes ao nível de proteção previsto na legislação brasileira.
        </p>
      </Section>

      <Section id="seguranca" title="9. Segurança da informação">
        <p>
          Adotamos medidas técnicas e administrativas razoáveis para proteger os dados
          pessoais contra acessos não autorizados, situações acidentais ou ilícitas de
          destruição, perda, alteração, comunicação ou difusão. Isso inclui, sem se limitar
          a: proteção de senhas por <i>hash</i>, criptografia de tokens sensíveis em repouso,
          controle de acesso, monitoramento e revisão periódica de nossos processos.
        </p>
        <p className="text-sm text-[#243B53]/70">
          Nenhum sistema é totalmente invulnerável. Não prometemos segurança absoluta,
          mas trabalhamos continuamente para reduzir riscos.
        </p>
      </Section>

      <Section id="retencao" title="10. Retenção">
        <p>
          Os dados pessoais são mantidos apenas pelo período necessário às finalidades para
          as quais foram coletados, ao cumprimento de obrigações legais, contratuais e ao
          exercício regular de direitos. Após esse período, os dados podem ser eliminados
          ou anonimizados, respeitados os prazos legais aplicáveis.
        </p>
      </Section>

      <Section id="direitos" title="11. Direitos dos titulares">
        <p>Nos termos da LGPD, o titular pode exercer, conforme aplicáveis, os direitos de:</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Confirmação da existência de tratamento;</li>
          <li>Acesso aos dados;</li>
          <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
          <li>Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade;</li>
          <li>Portabilidade dos dados, observadas normas e segredos comerciais/industriais;</li>
          <li>Eliminação dos dados tratados com base no consentimento;</li>
          <li>Informação sobre entidades públicas e privadas com as quais o controlador realiza uso compartilhado de dados;</li>
          <li>Revogação do consentimento;</li>
          <li>Oposição a tratamento realizado com fundamento em uma das hipóteses de dispensa de consentimento, em caso de descumprimento;</li>
          <li>Revisão de decisões automatizadas, quando aplicável.</li>
        </ul>
        <p>
          Os pedidos podem ser encaminhados pelos canais de atendimento disponibilizados
          no site do Clubinho da Fé. Poderemos solicitar informações adicionais para
          verificar a identidade do titular antes de atender à solicitação.
        </p>
      </Section>

      <Section id="cookies" title="12. Cookies e tecnologias técnicas">
        <p>
          Utilizamos cookies e tecnologias equivalentes estritamente necessários ao
          funcionamento da plataforma, à autenticação do usuário e à segurança. Não
          utilizamos, atualmente, cookies publicitários ou ferramentas de perfilamento de
          terceiros para fins de marketing direto.
        </p>
      </Section>

      <Section id="alteracoes" title="13. Alterações desta Política">
        <p>
          Esta Política pode ser atualizada periodicamente para refletir mudanças legais,
          técnicas ou operacionais. Alterações relevantes serão devidamente publicadas
          nesta página, indicando a data da última atualização.
        </p>
      </Section>

      <Section id="contato" title="14. Contato">
        <p>
          <b>Canal de privacidade:</b> consulte os canais de atendimento disponibilizados
          no site do Clubinho da Fé em
          {" "}<a href="https://clubinhodafeof.com" className="text-[#5DADE2] font-bold" target="_blank" rel="noopener noreferrer">clubinhodafeof.com</a>.
        </p>
        <p className="text-sm text-[#243B53]/70">
          Controlador: CREATIVE SHOOP — CNPJ 65.465.060/0001-25.
        </p>
      </Section>

      <div className="mt-12 flex flex-wrap gap-3 text-sm">
        <Link to="/terms" data-testid="privacy-to-terms" className="btn-outline">Ver Termos de Uso</Link>
        <Link to="/" data-testid="privacy-to-home" className="btn-outline">Voltar ao início</Link>
      </div>
    </div>
  );
}
